import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const services = [
  {
    title: "Hälsostart",
    subtitle: "Kom igång",
    description: "För dig som vill komma igång med träning och skapa hållbara vanor med professionellt stöd. En trygg start och tydlig plan.",
    price: "3 995 kr",
    features: [
      "Kostnadsfri konsultation",
      "90 min hälsokartläggning",
      "Målformulering",
      "Genomgång av hälsa och livsstil",
      "2 personliga träningspass",
      "Enklare individuellt träningsupplägg",
      "Skriftlig sammanfattning & rekommendationer"
    ]
  },
  {
    title: "Styrka & Balans",
    subtitle: "För utveckling",
    description: "För dig som vill utveckla din styrka och teknik, förbättra din hälsa och skapa kontinuitet. Vi arbetar tillsammans mot dina mål.",
    price: "10 995 kr",
    duration: "6–8 veckor",
    featured: true,
    features: [
      "Kostnadsfri konsultation",
      "90 min hälsokartläggning",
      "Målformulering",
      "Genomgång av din hälsa och livsstil",
      "6 personliga träningspass",
      "Individuellt träningsprogram & kostplan",
      "Veckovis avstämning & fri sms-kontakt (vardagar)",
      "Skriftlig sammanfattning & rekommendationer"
    ]
  },
  {
    title: "Premium",
    subtitle: "Livsstilsförändring",
    description: "För dig som vill göra en långsiktig investering i din hälsa. Mitt mest omfattande program med personligt helhetsupplägg.",
    price: "19 995 kr",
    duration: "10–12 veckor",
    features: [
      "Kostnadsfri konsultation",
      "90 min hälsokartläggning",
      "Målformulering",
      "10 personliga träningspass",
      "Personligt träningsprogram & kostplan",
      "Veckovis coachning och uppföljning",
      "Fri kontakt via sms på vardagar",
      "Justering av upplägg vid behov",
      "Skriftlig sammanfattning & rekommendationer"
    ]
  }
];

const Services = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '' });

  const handleOpenModal = (service) => {
    setSelectedService(service);
    setIsModalOpen(true);
    // Lock body scroll when modal is open
    document.body.style.overflow = 'hidden';
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => {
      setSelectedService(null);
      setFormData({ name: '', phone: '' });
      // Restore body scroll
      document.body.style.overflow = 'unset';
    }, 300); // Wait for transition
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedService) return;

    const subject = encodeURIComponent(`Bokning av paket: ${selectedService.title}`);
    const body = encodeURIComponent(
      `Hej Diana!\n\nJag vill gärna boka/veta mer om paketet "${selectedService.title}".\n\nMina kontaktuppgifter:\nNamn: ${formData.name}\nTelefon: ${formData.phone}\n\nMed vänliga hälsningar,\n${formData.name}`
    );
    
    // Open default mail client
    window.location.href = `mailto:hej@starkaremeddiana.se?subject=${subject}&body=${body}`;
    handleCloseModal();
  };

  return (
    <section id="tjanster" className="py-24 bg-white relative">
      <div className="container mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <span className="block text-sm uppercase tracking-[0.2em] text-stone-500 mb-4">
            Mina Tjänster
          </span>
          <h2 className="text-3xl md:text-5xl font-medium mb-6 font-serif text-stone-900">
            Investera i din hälsa
          </h2>
          <p className="text-stone-600 font-light">
            Alla nya klienter erbjuds en kostnadsfri konsultation där vi går igenom dina mål, behov och förutsättningar. Därefter rekommenderar jag det upplägg som passar dig bäst.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className={`relative flex flex-col p-8 md:p-10 border ${
                service.featured 
                  ? 'border-stone-900 bg-stone-900 text-white shadow-2xl' 
                  : 'border-stone-200 bg-[#FAFAFA] text-stone-900'
              }`}
            >
              <div className="mb-8">
                <span className={`block text-xs uppercase tracking-widest mb-2 ${
                  service.featured ? 'text-stone-400' : 'text-stone-500'
                }`}>
                  {service.subtitle}
                </span>
                <h3 className="text-2xl font-serif font-medium mb-4">{service.title}</h3>
                <p className={`font-light text-sm leading-relaxed min-h-[80px] ${
                  service.featured ? 'text-stone-300' : 'text-stone-600'
                }`}>
                  {service.description}
                </p>
              </div>

              <div className="mb-8">
                <div className="flex items-end gap-2 mb-1">
                  <span className="text-3xl font-medium">{service.price}</span>
                </div>
                {service.duration && (
                  <span className={`text-xs uppercase tracking-wider ${
                    service.featured ? 'text-stone-400' : 'text-stone-500'
                  }`}>
                    Längd: {service.duration}
                  </span>
                )}
              </div>

              <ul className="space-y-4 mb-10 flex-grow">
                {service.features.map((feature, fIndex) => (
                  <li key={fIndex} className="flex items-start gap-3">
                    <SafeIcon 
                      icon={FiIcons.FiCheck} 
                      className={`mt-1 flex-shrink-0 ${
                        service.featured ? 'text-stone-300' : 'text-stone-400'
                      }`} 
                    />
                    <span className={`text-sm font-light ${
                      service.featured ? 'text-stone-300' : 'text-stone-600'
                    }`}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <button 
                onClick={() => handleOpenModal(service)}
                className={`w-full py-4 text-xs uppercase tracking-widest transition-colors duration-300 ${
                  service.featured 
                    ? 'bg-white text-stone-900 hover:bg-stone-200' 
                    : 'bg-stone-900 text-white hover:bg-stone-800'
                }`}
              >
                Välj paket
              </button>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Booking Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-stone-900/40 backdrop-blur-sm"
            onClick={handleCloseModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white p-8 md:p-12 max-w-md w-full relative shadow-2xl border border-stone-100"
            >
              <button
                onClick={handleCloseModal}
                className="absolute top-6 right-6 text-stone-400 hover:text-stone-900 transition-colors"
                aria-label="Stäng"
              >
                <SafeIcon icon={FiIcons.FiX} className="text-2xl" />
              </button>

              <div className="mb-8">
                <span className="block text-xs uppercase tracking-widest text-stone-500 mb-2">
                  Bokningsförfrågan
                </span>
                <h3 className="text-2xl font-serif font-medium text-stone-900 mb-2">
                  {selectedService?.title}
                </h3>
                <p className="text-stone-600 font-light text-sm">
                  Fyll i dina uppgifter så skapar vi ett mail åt dig för att skicka iväg din förfrågan.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-xs uppercase tracking-widest text-stone-500 mb-2">
                    Ditt Namn
                  </label>
                  <input
                    id="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border-b border-stone-300 py-2 text-stone-900 focus:outline-none focus:border-stone-900 transition-colors bg-transparent font-light"
                    placeholder="För- och efternamn"
                  />
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-xs uppercase tracking-widest text-stone-500 mb-2">
                    Telefonnummer
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full border-b border-stone-300 py-2 text-stone-900 focus:outline-none focus:border-stone-900 transition-colors bg-transparent font-light"
                    placeholder="07X XXX XX XX"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-stone-900 text-white py-4 mt-4 text-xs uppercase tracking-widest hover:bg-stone-800 transition-colors flex items-center justify-center gap-2 group"
                >
                  <SafeIcon icon={FiIcons.FiMail} className="group-hover:scale-110 transition-transform" />
                  Fortsätt till mail
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Services;